import { useApiQuery } from '../useApiQuery';
import { operationsClient, apiGet } from '@/lib/api';
import { publicGet } from '@/lib/api/publicClient';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { CoverageResponse } from '@/types/scheduler';

export interface UseCoverageOptions {
  /**
   * When true, call the endpoint via the unauthenticated public client. Use
   * this from customer-facing surfaces (online booking) that don't have a
   * tenant session. Defaults to false (operator surfaces).
   *
   * Also implicitly enabled whenever a `tenantId` is supplied — the only
   * reason a caller would know its tenant id ahead of time is because it's
   * reading it from a public URL path.
   */
  publicClient?: boolean;
}

/**
 * Checks whether the tenant has technicians who can cover a given zip code
 * (optionally narrowed to a specific service type). Powers the customer-
 * coverage warning banner on scheduling surfaces.
 *
 * Disabled until a zip code is provided — there's no useful answer without it.
 * Errors are not toasted; coverage is informational and shouldn't interrupt
 * scheduling flows when the lookup transiently fails.
 *
 * `tenantId` is required only for the **public** call (the path-scoped public
 * endpoint takes it from the URL). For internal/operator surfaces the tenant
 * comes from the auth session header, so the argument is omitted.
 */
export function useCoverage(
  zipCode?: string | null,
  serviceTypeId?: string | null,
  options: UseCoverageOptions = {},
  tenantId?: string | null,
) {
  const normalizedZip = (zipCode ?? '').trim();
  const normalizedServiceTypeId = serviceTypeId?.trim() || null;
  const normalizedTenantId = tenantId?.trim() || null;
  // Either an explicit publicClient flag OR the presence of a tenantId routes
  // us to the public endpoint — the latter is what the online-booking page
  // does (it has the tenant id on hand because it's right there in the URL).
  const usePublic = options.publicClient === true || Boolean(normalizedTenantId);

  return useApiQuery<CoverageResponse>({
    queryKey: [
      ...schedulerKeys.coverage(normalizedZip, normalizedServiceTypeId),
      usePublic ? 'public' : 'internal',
      normalizedTenantId ?? 'session-tenant',
    ],
    queryFn: () => {
      const params = {
        zipCode: normalizedZip,
        ...(normalizedServiceTypeId ? { serviceTypeId: normalizedServiceTypeId } : {}),
      };
      if (usePublic) {
        // The public endpoint requires a tenantId in the path. Without one the
        // call would 404 — guard the query so it stays disabled until the
        // caller supplies the id (the online-booking route always has it).
        if (!normalizedTenantId) {
          return Promise.reject(
            new Error('useCoverage: tenantId is required when publicClient is enabled.'),
          );
        }
        return publicGet<CoverageResponse>(
          schedulerEndpoints.publicCoverage(normalizedTenantId),
          params,
        );
      }
      return apiGet<CoverageResponse>(operationsClient, schedulerEndpoints.coverage(), {
        params,
      });
    },
    // For the internal path, a zip is enough. For the public path we also need
    // the tenantId; otherwise the URL wouldn't be well-formed.
    enabled: Boolean(normalizedZip) && (!usePublic || Boolean(normalizedTenantId)),
    showErrorToast: false,
    // Coverage rarely changes mid-session; let it sit a bit before refetching.
    staleTime: 60_000,
    // Don't retry on the public path — if the endpoint isn't exposed publicly
    // the call will keep 401-ing; better to degrade silently.
    retry: usePublic ? false : undefined,
  });
}
